/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$headline-big-shadow: 0px 1px 4px rgba(57, 57, 58, 0.2) !default;
$headline-small-shadow: 0px 1px 2px rgba(57, 57, 58, 0.2) !default;
$headline-tiny-shadow: 0px 1px 1px rgba(57, 57, 58, 0.2) !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

@mixin hyphenation {
  hyphens: auto;
  overflow-wrap: anywhere;
  /*toDo:
  when support for hyphenation configuration grows, still clarify configuration with designers */
  hyphenate-limit-chars: 10 3 2;
}

:host {
  color: v(color);
  transition: color $transition-duration ease-in-out;
  visibility: visible;
}

.#{$prefix}no-margin {
  margin: 0;
}

.#{$prefix}font-weight-regular {
  font-weight: $font-weight-regular !important;
}

.#{$prefix}is-hidden {
  visibility: hidden;
}

.#{$prefix}heading-h1 {
  color: v(h1Color);
  @include eon-typography("h1");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-big-shadow;

    @include for-size(phone) {
      text-shadow: $headline-small-shadow;
    }
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 30px 16px 0;

    @include for-size(desktop) {
      margin-top: 24px;
    }

    @include for-size(tablet) {
      margin-top: 18px;
    }

    @include for-size(phone) {
      margin-top: 12px;
    }
  }
}

.#{$prefix}heading-h2 {
  @include eon-typography("h2");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-big-shadow;

    @include for-size(tablet) {
      text-shadow: $headline-small-shadow;
    }

    @include for-size(phone) {
      text-shadow: $headline-small-shadow;
    }
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 24px 16px 0;

    @include for-size(desktop) {
      margin-top: 17px;
    }

    @include for-size(tablet) {
      margin-top: 11px;
    }

    @include for-size(phone) {
      margin-top: 8px;
    }
  }
}

.#{$prefix}heading-h3 {
  @include eon-typography("h3");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-small-shadow;
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 14px 16px 0;

    @include for-size(desktop) {
      margin-top: 11px;
    }

    @include for-size(tablet) {
      margin-top: 8px;
    }

    @include for-size(phone) {
      margin-top: 5px;
    }
  }
}

.#{$prefix}heading-h4 {
  @include eon-typography("h4");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-small-shadow;

    @include for-size(phone) {
      text-shadow: $headline-tiny-shadow;
    }
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 6px 16px 0;

    @include for-size(desktop-or-smaller) {
      margin-top: 3px;
    }

    @include for-size(phone) {
      margin-top: 1px;
    }
  }
}

.#{$prefix}heading-h5 {
  @include eon-typography("h5");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-tiny-shadow;
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 1px 16px 0;

    @include for-size(desktop) {
      margin-top: 0;
    }
  }
}

.#{$prefix}heading-h6 {
  @include eon-typography("h6");

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  &.#{$prefix}text-shadow {
    text-shadow: $headline-tiny-shadow;
  }

  .#{$prefix}headline-info-icon {
    align-items: flex-start;
    margin: 1px 16px 0;

    @include for-size(desktop) {
      margin-top: 0;
    }
  }
}

.#{$prefix}heading-copy.#{$prefix}copy {
  @include eon-typography("copy");
  display: block;

  &.#{$prefix}hyphenation {
    @include hyphenation;
  }

  font-weight: 500;
}

.#{$prefix}headline {
  &.#{$prefix}right {
    text-align: right;
  }

  &.#{$prefix}left {
    text-align: left;
  }

  &.#{$prefix}center {
    text-align: center;
  }

  &.#{$prefix}ignore-alignment {
    text-align: unset;
  }
}

.#{$prefix}info-icon-display {
  display: flex;

  &.#{$prefix}center {
    justify-content: center;
  }
}

:host(.container-breakpoint-1200) {
  .#{$prefix}container-based-size {
    &.#{$prefix}heading-h1 {
      font-size: $heading-h1-font-size-desktop;
      line-height: $heading-h1-line-height-desktop;
    }

    &.#{$prefix}heading-h2 {
      font-size: $heading-h2-font-size-desktop;
      line-height: $heading-h2-line-height-desktop;
    }

    &.#{$prefix}heading-h3 {
      font-size: $heading-h3-font-size-desktop;
      line-height: $heading-h3-line-height-desktop;
    }

    &.#{$prefix}heading-h4 {
      font-size: $heading-h4-font-size-desktop;
      line-height: $heading-h4-line-height-desktop;
    }

    &.#{$prefix}heading-h5 {
      font-size: $heading-h5-font-size-desktop;
      line-height: $heading-h5-line-height-desktop;
    }

    &.#{$prefix}heading-h6 {
      font-size: $heading-h6-font-size-desktop;
      line-height: $heading-h6-line-height-desktop;
    }
  }
}

:host(.container-breakpoint-800) {
  .#{$prefix}container-based-size {
    &.#{$prefix}heading-h1 {
      font-size: $heading-h1-font-size-tablet;
      line-height: $heading-h1-line-height-tablet;
    }

    &.#{$prefix}heading-h2 {
      font-size: $heading-h2-font-size-tablet;
      line-height: $heading-h2-line-height-tablet;
    }

    &.#{$prefix}heading-h3 {
      font-size: $heading-h3-font-size-tablet;
      line-height: $heading-h3-line-height-tablet;
    }

    &.#{$prefix}heading-h4 {
      font-size: $heading-h4-font-size-tablet;
      line-height: $heading-h4-line-height-tablet;
    }

    &.#{$prefix}heading-h5 {
      font-size: $heading-h5-font-size-tablet;
      line-height: $heading-h5-line-height-tablet;
    }

    &.#{$prefix}heading-h6 {
      font-size: $heading-h6-font-size-tablet;
      line-height: $heading-h6-line-height-tablet;
    }
  }
}

:host(.container-breakpoint-400) {
  .#{$prefix}container-based-size {
    &.#{$prefix}heading-h1 {
      font-size: $heading-h1-font-size-phone;
      line-height: $heading-h1-line-height-phone;
    }

    &.#{$prefix}heading-h2 {
      font-size: $heading-h2-font-size-phone;
      line-height: $heading-h2-line-height-phone;
    }

    &.#{$prefix}heading-h3 {
      font-size: $heading-h3-font-size-phone;
      line-height: $heading-h3-line-height-phone;
    }

    &.#{$prefix}heading-h4 {
      font-size: $heading-h4-font-size-phone;
      line-height: $heading-h4-line-height-phone;
    }

    &.#{$prefix}heading-h5 {
      font-size: $heading-h5-font-size-phone;
      line-height: $heading-h5-line-height-phone;
    }

    &.#{$prefix}heading-h6 {
      font-size: $heading-h6-font-size-phone;
      line-height: $heading-h6-line-height-phone;
    }
  }
}
